/**
 * Cross-store country picker (see snippets/country-selector-stores.liquid).
 *
 * Mirrors the disclosure open/close behaviour of `localization-form`, but the
 * items are ordinary cross-domain links to the sibling storefront, so clicks
 * must navigate. LocalizationForm calls preventDefault() on every item and
 * submits a form instead, which would swallow those links — hence a separate
 * element rather than a flag on that one.
 *
 * Entries rendered with href="#" are served by THIS store, so they are chosen
 * in place instead: the choice is remembered in localStorage and every picker
 * on the page re-labels itself. On the AU store that covers both Australia and
 * New Zealand, which the AU storefront ships to.
 *
 * CAVEAT: an in-place choice is a display preference only. There is no NZ
 * market, so pricing, shipping and checkout stay AUD whichever of the two is
 * showing. Once an NZ market exists this should become a real market switch.
 */
const STORE_SELECTOR_COUNTRY_KEY = 'takimai:selected-country';
const STORE_SELECTOR_CHANGE_EVENT = 'store-selector:change';
if (!customElements.get('store-selector')) {
  customElements.define(
    'store-selector',
    class StoreSelector extends HTMLElement {
      constructor() {
        super();
        this.header = document.querySelector('.header-wrapper');
        this.button = this.querySelector('button.localization-form__select');
        this.panel = this.querySelector('.disclosure__list-wrapper');
        this.closeButton = this.querySelector('.country-selector__close-button');

        if (!this.button || !this.panel) return;

        this.addEventListener('keyup', this.onContainerKeyUp.bind(this));
        this.addEventListener('keydown', this.onContainerKeyDown.bind(this));
        this.addEventListener('focusout', this.closeSelector.bind(this));
        this.button.addEventListener('click', this.openSelector.bind(this));

        if (this.closeButton) {
          this.closeButton.addEventListener('click', this.hidePanel.bind(this));
        }

        this.disclosure = this.querySelector('.disclosure');
        this.label = this.querySelector('[data-country-label]');
        this.defaultCountry = this.disclosure ? this.disclosure.dataset.defaultCountry : null;

        // Entries this store serves itself (href="#") are chosen in place;
        // anything else is a real link to the other store and is left alone.
        this.querySelectorAll('a[href="#"]').forEach((item) => {
          item.addEventListener('click', (event) => {
            event.preventDefault();
            this.selectCountry(item.dataset.value);
            this.hidePanel();
          });
        });

        document.addEventListener(STORE_SELECTOR_CHANGE_EVENT, (event) => {
          this.render(event.detail.code);
        });

        const stored = this.storedCountry();
        if (stored && stored !== this.defaultCountry) this.render(stored);
      }

      /** Only codes this store serves in place are valid; anything else is stale. */
      storedCountry() {
        let code = null;
        try {
          code = localStorage.getItem(STORE_SELECTOR_COUNTRY_KEY);
        } catch (error) {
          return null; // private mode / storage disabled
        }
        return code && this.querySelector(`a[href="#"][data-value="${code}"]`) ? code : null;
      }

      selectCountry(code) {
        try {
          if (code === this.defaultCountry) {
            localStorage.removeItem(STORE_SELECTOR_COUNTRY_KEY);
          } else {
            localStorage.setItem(STORE_SELECTOR_COUNTRY_KEY, code);
          }
        } catch (error) {
          // Storage unavailable: the choice just won't survive the next page.
        }
        document.dispatchEvent(new CustomEvent(STORE_SELECTOR_CHANGE_EVENT, { detail: { code } }));
      }

      render(code) {
        const selected = this.querySelector(`a[data-value="${code}"]`);
        if (!selected || !this.label) return;

        this.label.textContent = `${selected.dataset.countryName} | ${selected.dataset.currency} $`;

        this.querySelectorAll('a[data-value]').forEach((item) => {
          const isSelected = item === selected;
          const check = item.querySelector('[data-country-check]');
          if (isSelected) {
            item.setAttribute('aria-current', 'true');
          } else {
            item.removeAttribute('aria-current');
          }
          if (check) check.classList.toggle('visibility-hidden', !isSelected);
        });
      }

      get menuDrawer() {
        return document.querySelector('.menu-drawer');
      }

      hidePanel() {
        this.button.setAttribute('aria-expanded', 'false');
        this.panel.setAttribute('hidden', true);
        document.body.classList.remove('overflow-hidden-mobile');
        if (this.menuDrawer) this.menuDrawer.classList.remove('country-selector-open');
        if (this.header) this.header.preventHide = false;
      }

      openSelector() {
        this.button.focus();
        this.panel.toggleAttribute('hidden');
        this.button.setAttribute(
          'aria-expanded',
          (this.button.getAttribute('aria-expanded') === 'false').toString()
        );
        if (!document.body.classList.contains('overflow-hidden-tablet')) {
          document.body.classList.add('overflow-hidden-mobile');
        }
        if (this.hasAttribute('data-prevent-hide') && this.header) {
          this.header.preventHide = true;
        }
        if (this.menuDrawer) this.menuDrawer.classList.add('country-selector-open');
      }

      closeSelector(event) {
        if (
          event.target.classList.contains('country-selector__overlay') ||
          !this.contains(event.target) ||
          !this.contains(event.relatedTarget)
        ) {
          this.hidePanel();
        }
      }

      onContainerKeyUp(event) {
        event.preventDefault();

        switch (event.code.toUpperCase()) {
          case 'ESCAPE':
            if (this.button.getAttribute('aria-expanded') === 'false') return;
            this.hidePanel();
            event.stopPropagation();
            this.button.focus();
            break;
          case 'SPACE':
            if (this.button.getAttribute('aria-expanded') === 'true') return;
            this.openSelector();
            break;
        }
      }

      onContainerKeyDown(event) {
        const items = Array.from(this.querySelectorAll('a'));
        const focusedIndex = items.findIndex((item) => item === document.activeElement);
        let itemToFocus;

        switch (event.code.toUpperCase()) {
          case 'ARROWUP':
            event.preventDefault();
            itemToFocus = focusedIndex > 0 ? items[focusedIndex - 1] : items[items.length - 1];
            itemToFocus.focus();
            break;
          case 'ARROWDOWN':
            event.preventDefault();
            itemToFocus = focusedIndex < items.length - 1 ? items[focusedIndex + 1] : items[0];
            itemToFocus.focus();
            break;
        }
      }
    }
  );
}
